import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Users,
  Plus,
  Video,
  Clock
} from 'lucide-react';

const categoryImages = {
  decouverte: 'https://images.unsplash.com/photo-1625479142928-c2f2914318f2?w=400',
  complete: 'https://images.unsplash.com/photo-1676761136925-900da9b58200?w=400',
  auto: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400',
  moto: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=400',
  nautique: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400',
  habitat: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400',
  dessin: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400'
};

export const FormateurDashboard = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [coursesData, studentsData] = await Promise.all([
        api.getInstructorCourses(),
        api.getInstructorStudents()
      ]);
      setCourses(coursesData);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentProgress = (enrollment) => {
    const course = enrollment.course;
    if (!course?.episodes?.length) return 0;
    const completed = Object.values(enrollment.progress || {}).filter(Boolean).length;
    return (completed / course.episodes.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-beige border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2" data-testid="formateur-title">
            Espace formateur
          </h1>
          <p className="text-muted-foreground">
            Bienvenue {user?.first_name}, gérez vos formations et suivez vos élèves
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#1F2A4A]/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-5 h-5 text-[#C9B896]" />
              <span className="text-muted-foreground">Mes formations</span>
            </div>
            <div className="text-3xl font-heading font-bold">{courses.length}</div>
          </div>
          <div className="bg-white border border-[#1F2A4A]/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#C9B896]" />
              <span className="text-muted-foreground">Mes élèves</span>
            </div>
            <div className="text-3xl font-heading font-bold">{students.length}</div>
          </div>
          <div className="bg-white border border-[#1F2A4A]/10 rounded-lg p-6 col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-2">
              <Video className="w-5 h-5 text-[#C9B896]" />
              <span className="text-muted-foreground">Épisodes</span>
            </div>
            <div className="text-3xl font-heading font-bold">
              {courses.reduce((acc, c) => acc + (c.episodes?.length || 0), 0)}
            </div>
          </div>
        </div>

        <Tabs defaultValue="courses" className="space-y-6">
          <TabsList className="bg-white border border-[#1F2A4A]/10">
            <TabsTrigger value="courses" className="data-[state=active]:bg-[#C9B896] data-[state=active]:text-black">
              <BookOpen className="w-4 h-4 mr-2" />
              Mes formations
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-[#C9B896] data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Mes élèves
            </TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            {courses.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-lg">
                <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Aucune formation</h3>
                <p className="text-muted-foreground">
                  Vous n'avez pas encore créé de formation.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {courses.map((course) => (
                  <div 
                    key={course.id}
                    className="bg-white border border-[#1F2A4A]/10 rounded-lg overflow-hidden"
                    data-testid={`formateur-course-${course.id}`}
                  >
                    <div className="aspect-video relative">
                      <img 
                        src={course.thumbnail_url || categoryImages[course.category] || categoryImages.complete}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4">
                        <Badge variant={course.is_published ? 'default' : 'outline'} className={course.is_published ? 'bg-green-500/20 text-green-500' : ''}>
                          {course.is_published ? 'Publié' : 'Brouillon'}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold mb-2">{course.title}</h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {course.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Video className="w-4 h-4" />
                          {course.episodes?.length || 0} épisodes
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {students.filter(s => s.course_id === course.id).length} élèves
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            {students.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-lg">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Aucun élève</h3>
                <p className="text-muted-foreground">
                  Aucun élève n'est inscrit à vos formations pour le moment.
                </p>
              </div>
            ) : (
              <div className="bg-white border border-[#1F2A4A]/10 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1F2A4A]/10">
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Élève</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Formation</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Progression</th>
                        <th className="text-left p-4 text-sm font-medium text-muted-foreground">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((enrollment) => {
                        const progress = getStudentProgress(enrollment);
                        const user = enrollment.user;
                        const course = enrollment.course;

                        return (
                          <tr key={enrollment.id} className="border-b border-[#1F2A4A]/10 last:border-0">
                            <td className="p-4">
                              <div>
                                <div className="font-medium">{user?.first_name} {user?.last_name}</div>
                                <div className="text-sm text-muted-foreground">{user?.email}</div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">{course?.title}</div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <Progress value={progress} className="h-2 w-24" />
                                <span className="text-sm font-mono">{Math.round(progress)}%</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant={progress === 100 ? 'default' : progress > 0 ? 'secondary' : 'outline'} 
                                     className={progress === 100 ? 'bg-green-500/20 text-green-500' : ''}>
                                {progress === 100 ? 'Terminé' : progress > 0 ? 'En cours' : 'Non démarré'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
